import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  CYLINDER_RANGE,
  EYES,
  REFRACTION_OUTCOMES,
  REFRACTION_TESTERS,
  SPHERE_RANGE,
  VISUAL_ACUITY_NOTATIONS,
  branchDate,
  dioptre,
  formatDioptre,
  isWithin,
  referralProblem,
  resolveTester,
  validateAdd,
  validateAxis,
  visualAcuityProblem,
  type RefractionOutcome,
  type RefractionTester,
  type VisualAcuityNotation,
} from '@lensmira/domain'
import { formatDay } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * The refraction record: what a sight test produced and who did it. §6.38, `REF-001` to `REF-006`.
 *
 * `refraction` had 27 columns and nothing wrote them, so a shop could record the prescription a
 * test produced and nothing about the test. This is the form and the history, a panel on the
 * customer record like `Fittings`.
 *
 * **There is no reason box, and that is `REF-006`.** "The reason is not recorded in clinical
 * terms... to prevent the product becoming a place where clinical judgements are written down by
 * people not qualified to write them." A referral is who and when. Adding a field here to say why
 * would undo §6.38, and `refractions.test.tsx` fails if a free-text input appears beyond the ones
 * the requirement names.
 *
 * **Every acuity is sent with its notation**, because "a bare 6/9 means nothing without it". One
 * selector covers the values being entered together, and each value in the request carries it.
 *
 * **The form refuses locally what the server would refuse.** A refusal from a queued write blocks
 * everything behind it until somebody discards it, so the same `packages/domain` functions the API
 * uses (`resolveTester`, `referralProblem`, `visualAcuityProblem`, Appendix I's axis and add) are
 * asked here first. The server still checks all of it: a queue posts JSON.
 *
 * **Not on this screen:** PD at the test (whole millimetres only, so a real 31.5 cannot be stored,
 * which is a schema problem), binocular acuity and the session and practice of a visiting or
 * external tester (the schema has no column for them), and the test fee (`REF-007`).
 */

export interface RefractionSetData {
  readonly sphere: number
  readonly cylinder: number
  readonly axis?: number
  readonly add?: number
}

export interface RefractionAcuityData {
  readonly value: string
  readonly notation: string
}

export interface RefractionEyeData {
  readonly objective?: RefractionSetData
  readonly subjective?: RefractionSetData
  readonly oldGlasses?: RefractionSetData
  readonly unaided?: RefractionAcuityData
  readonly aided?: RefractionAcuityData
  readonly pinhole?: RefractionAcuityData
  readonly pdMonocularMm?: number
}

export interface RefractionData {
  readonly id: string
  /** An instant. Rendered as the day in the branch's zone (Appendix J). */
  readonly performedAt: string
  readonly branchId: string | null
  readonly tester: {
    readonly kind: string
    readonly staffId?: string
    readonly name?: string
    readonly registration?: string
  }
  readonly outcome: string
  readonly referral?: { readonly referredTo: string; readonly referredOn: string }
  readonly nearAdd?: number
  readonly workingDistanceCm?: number
  readonly dominantEye?: string
  readonly pdBinocularMm?: number
  readonly prescriptionIds: readonly string[]
  readonly od?: RefractionEyeData
  readonly os?: RefractionEyeData
}

export interface RefractionsProps {
  readonly customerId: string
  /** The branch the test happens at: where the signed-in person is working. */
  readonly branchId: string
  /** The signed-in member of staff, named as the tester for the two in-house kinds. */
  readonly staffId: string
  readonly timezone: string
  /** Appendix B's `prescription_create_version`. */
  readonly canRecord: boolean
  /** Appendix B's `prescription_view`. */
  readonly canView: boolean
  readonly loadRefractions: () => Promise<readonly RefractionData[]>
  readonly onRecorded?: () => void
}

const TESTER_KEY = {
  in_house_doctor: 'refraction.tester.inHouseDoctor',
  in_house_refractionist: 'refraction.tester.inHouseRefractionist',
  visiting_optometrist: 'refraction.tester.visitingOptometrist',
  external_prescriber: 'refraction.tester.externalPrescriber',
  customer_supplied_unknown: 'refraction.tester.customerSuppliedUnknown',
} as const

const OUTCOME_KEY = {
  prescription_issued: 'refraction.outcome.prescriptionIssued',
  no_change: 'refraction.outcome.noChange',
  referred_out: 'refraction.outcome.referredOut',
} as const

const TESTER_HELP_KEY = {
  in_house_doctor: 'refraction.testerHelp.inHouseDoctor',
  in_house_refractionist: 'refraction.testerHelp.inHouseRefractionist',
  visiting_optometrist: 'refraction.testerHelp.visitingOptometrist',
  external_prescriber: 'refraction.testerHelp.externalPrescriber',
  customer_supplied_unknown: 'refraction.testerHelp.customerSuppliedUnknown',
} as const

const VA_KEY = {
  unaided: 'refraction.va.unaided',
  aided: 'refraction.va.aided',
  pinhole: 'refraction.va.pinhole',
} as const

/** What a value looks like in each notation, as the box's placeholder. Data, not words. */
const ACUITY_EXAMPLE: Record<VisualAcuityNotation, string> = {
  metric_6: '6/6',
  imperial_20: '20/20',
  logmar: '0.00',
}

const NOTATION_KEY = {
  metric_6: 'refraction.notation.metric6',
  imperial_20: 'refraction.notation.imperial20',
  logmar: 'refraction.notation.logmar',
} as const

interface SetForm {
  sphere: string
  cylinder: string
  axis: string
  add: string
}

interface EyeForm {
  objective: SetForm
  subjective: SetForm
  oldGlasses: SetForm
  unaided: string
  aided: string
  pinhole: string
}

const emptySet = (): SetForm => ({ sphere: '', cylinder: '', axis: '', add: '' })
const emptyEye = (): EyeForm => ({
  objective: emptySet(),
  subjective: emptySet(),
  oldGlasses: emptySet(),
  unaided: '',
  aided: '',
  pinhole: '',
})

type SetName = 'objective' | 'subjective' | 'oldGlasses'
const SETS: readonly SetName[] = ['objective', 'subjective', 'oldGlasses']
const SET_LABEL = {
  objective: 'refraction.set.objective',
  subjective: 'refraction.set.subjective',
  oldGlasses: 'refraction.set.oldGlasses',
} as const

/** An empty box is "not entered"; anything else is a number, or NaN, which is never sendable. */
const num = (text: string): number | undefined => (text.trim() === '' ? undefined : Number(text))

function formatPower(value: number): string {
  try {
    return formatDioptre(dioptre(value))
  } catch {
    return String(value)
  }
}

const setText = (set: RefractionSetData): string =>
  `${formatPower(set.sphere)} ${formatPower(set.cylinder)}${
    set.axis === undefined ? '' : ` x ${String(set.axis)}`
  }${set.add === undefined ? '' : ` add ${formatPower(set.add)}`}`

interface Built {
  readonly body?: Record<string, unknown>
  readonly problem?: string
}

export function Refractions({
  customerId,
  branchId,
  staffId,
  timezone,
  canRecord,
  canView,
  loadRefractions,
  onRecorded,
}: RefractionsProps): JSX.Element | null {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [records, setRecords] = useState<readonly RefractionData[]>([])
  const [loadFailed, setLoadFailed] = useState(false)
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<RefractionTester>('in_house_refractionist')
  const [testerName, setTesterName] = useState('')
  const [registration, setRegistration] = useState('')
  const [outcome, setOutcome] = useState<RefractionOutcome>('prescription_issued')
  const [referredTo, setReferredTo] = useState('')
  const [referredOn, setReferredOn] = useState(() => branchDate(Date.now(), timezone))
  const [notation, setNotation] = useState<VisualAcuityNotation>('metric_6')
  const [eyes, setEyes] = useState<{ od: EyeForm; os: EyeForm }>(() => ({
    od: emptyEye(),
    os: emptyEye(),
  }))
  const [nearAdd, setNearAdd] = useState('')
  const [workingDistance, setWorkingDistance] = useState('')
  const [dominantEye, setDominantEye] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const [blurred, setBlurred] = useState<ReadonlySet<string>>(new Set())
  const errorRef = useRef<HTMLParagraphElement>(null)

  const reload = useCallback(() => {
    if (!canView) return
    loadRefractions().then(
      (loaded) => {
        setRecords(loaded)
        setLoadFailed(false)
      },
      () => {
        setLoadFailed(true)
      },
    )
  }, [canView, loadRefractions])

  useEffect(() => {
    reload()
  }, [reload])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setOpen(false)
    setEyes({ od: emptyEye(), os: emptyEye() })
    setNearAdd('')
    setWorkingDistance('')
    setDominantEye('')
    setReferredTo('')
    setBlurred(new Set())
    reload()
    onRecorded?.()
  })

  if (!canView && !canRecord) return null

  const needsName = kind === 'visiting_optometrist' || kind === 'external_prescriber'
  const needsRegistration = kind !== 'in_house_refractionist' && kind !== 'customer_supplied_unknown'
  const inHouse = kind === 'in_house_doctor' || kind === 'in_house_refractionist'

  /** One reading set, after Appendix I. Nothing typed is `undefined`; a bad value is a message. */
  const readSet = (
    label: string,
    side: 'od' | 'os',
    form: SetForm,
    allowAdd: boolean,
  ): { readonly set?: Record<string, number>; readonly problem?: string } => {
    const sphere = num(form.sphere)
    const cylinder = num(form.cylinder)
    const axis = num(form.axis)
    const add = allowAdd ? num(form.add) : undefined
    if (
      sphere === undefined &&
      cylinder === undefined &&
      axis === undefined &&
      add === undefined
    ) {
      return {}
    }
    const where = `${label}, ${t(side === 'od' ? 'customerDetail.rx.row.right' : 'customerDetail.rx.row.left')}`
    if (sphere === undefined) return { problem: t('refraction.sphereRequired', { where }) }
    try {
      if (!isWithin(dioptre(sphere), SPHERE_RANGE)) {
        return { problem: t('refraction.powerInvalid', { where }) }
      }
      if (!isWithin(dioptre(cylinder ?? 0), CYLINDER_RANGE)) {
        return { problem: t('refraction.powerInvalid', { where }) }
      }
      const blocking = validateAxis(axis, dioptre(cylinder ?? 0)).find((f) => f.severity === 'block')
      if (blocking) return { problem: `${where}: ${blocking.message}` }
      if (add !== undefined) {
        const addBlock = validateAdd(dioptre(add)).find((f) => f.severity === 'block')
        if (addBlock) return { problem: `${where}: ${addBlock.message}` }
      }
    } catch {
      // `dioptre()` throws on an off-step or non-numeric value. Say so as a sentence.
      return { problem: t('refraction.powerInvalid', { where }) }
    }
    return {
      set: {
        sphere,
        ...(cylinder === undefined ? {} : { cylinder }),
        ...(axis === undefined ? {} : { axis }),
        ...(add === undefined ? {} : { add }),
      },
    }
  }

  const build = (): Built => {
    const resolved = resolveTester({
      kind,
      staffId: inHouse ? staffId : undefined,
      name: testerName,
      registration,
    })
    if (!resolved.ok) return { problem: resolved.message }

    const referral = referralProblem(
      outcome,
      outcome === 'referred_out' ? referredTo : undefined,
      outcome === 'referred_out' ? referredOn : undefined,
    )
    if (referral !== null) return { problem: referral.message }

    const body: Record<string, unknown> = {
      branchId,
      tester: {
        kind,
        ...(resolved.tester.staffId === null ? {} : { staffId: resolved.tester.staffId }),
        ...(resolved.tester.name === null ? {} : { name: resolved.tester.name }),
        ...(resolved.tester.registration === null
          ? {}
          : { registration: resolved.tester.registration }),
      },
      outcome,
      ...(outcome === 'referred_out' ? { referredTo: referredTo.trim(), referredOn } : {}),
    }

    for (const side of EYES) {
      const form = eyes[side]
      const eye: Record<string, unknown> = {}
      for (const name of SETS) {
        const read = readSet(t(SET_LABEL[name]), side, form[name], name === 'oldGlasses')
        if (read.problem !== undefined) return { problem: read.problem }
        if (read.set !== undefined) eye[name] = read.set
      }
      for (const name of ['unaided', 'aided', 'pinhole'] as const) {
        const value = form[name].trim()
        if (value === '') continue
        const problem = visualAcuityProblem(value, notation)
        if (problem !== null) return { problem: problem.message }
        // The notation travels with every value: "a bare 6/9 means nothing without it".
        eye[name] = { value, notation }
      }
      if (Object.keys(eye).length > 0) body[side] = eye
    }

    const add = num(nearAdd)
    const distance = num(workingDistance)
    if (add !== undefined) {
      try {
        const blocking = validateAdd(dioptre(add)).find((f) => f.severity === 'block')
        if (blocking) return { problem: blocking.message }
      } catch {
        return { problem: t('refraction.nearAddInvalid') }
      }
      if (distance === undefined) return { problem: t('refraction.workingDistanceRequired') }
      body['nearAdd'] = add
    }
    if (distance !== undefined) {
      if (!Number.isInteger(distance) || distance <= 0) {
        return { problem: t('refraction.workingDistanceInvalid') }
      }
      body['workingDistanceCm'] = distance
    }
    if (dominantEye !== '') body['dominantEye'] = dominantEye
    return { body }
  }

  const record = async (): Promise<void> => {
    const built = build()
    if (built.body === undefined) {
      setError(built.problem ?? null)
      // A refusal at the top of a long form is off-screen from the button that caused it.
      setTimeout(() => errorRef.current?.focus(), 0)
      return
    }
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: `/customers/${encodeURIComponent(customerId)}/refractions`,
      label: t('refraction.queueLabel'),
      body: built.body,
    })) as { id: string }
    setWriteId(write.id)
  }

  const setEyeField = (
    side: 'od' | 'os',
    name: SetName,
    field: keyof SetForm,
    value: string,
  ): void => {
    setEyes((prev) => ({
      ...prev,
      [side]: { ...prev[side], [name]: { ...prev[side][name], [field]: value } },
    }))
  }
  const setAcuity = (side: 'od' | 'os', name: 'unaided' | 'aided' | 'pinhole', value: string) => {
    setEyes((prev) => ({ ...prev, [side]: { ...prev[side], [name]: value } }))
  }

  const sideLabel = (side: 'od' | 'os'): string =>
    t(side === 'od' ? 'customerDetail.rx.row.right' : 'customerDetail.rx.row.left')

  const acuityText = (acuity: RefractionAcuityData | undefined) => {
    if (acuity === undefined) {
      return <span className="lm-refraction__none">{t('common.notRecorded')}</span>
    }
    const known = (VISUAL_ACUITY_NOTATIONS as readonly string[]).includes(acuity.notation)
    return (
      <>
        <Ltr>{acuity.value}</Ltr>{' '}
        <span className="lm-refraction__notation">
          {known ? t(NOTATION_KEY[acuity.notation as VisualAcuityNotation]) : acuity.notation}
        </span>
      </>
    )
  }
  const setCell = (set: RefractionSetData | undefined) =>
    set === undefined ? (
      <span className="lm-refraction__none">{t('common.notRecorded')}</span>
    ) : (
      <Ltr>{setText(set)}</Ltr>
    )

  const day = (value: string): string => formatDay(value, timezone, locale)
  const line = statusLine(t, snapshot, writeId, t('queue.subject.refraction'))

  /** A number box. `aria-label` carries the full name so a screen reader hears "Sphere, Subjective, Right". */
  const cell = (
    ariaLabel: string,
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
  ) => (
    <input
      className="lm-input lm-refraction__cell"
      type="text"
      inputMode="decimal"
      dir="ltr"
      autoComplete="off"
      aria-label={ariaLabel}
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        onChange(e.currentTarget.value)
      }}
    />
  )

  const acuityCell = (side: 'od' | 'os', name: 'unaided' | 'aided' | 'pinhole') => {
    const value = eyes[side][name]
    const id = `${side}.${name}`
    const problem =
      blurred.has(id) && value.trim() !== '' ? visualAcuityProblem(value.trim(), notation) : null
    return (
      <td>
        <input
          className="lm-input lm-refraction__cell"
          type="text"
          dir="ltr"
          autoComplete="off"
          aria-label={`${t(VA_KEY[name])}, ${sideLabel(side)}`}
          aria-invalid={problem === null ? undefined : true}
          placeholder={ACUITY_EXAMPLE[notation]}
          value={value}
          onChange={(e) => {
            setAcuity(side, name, e.currentTarget.value)
          }}
          onBlur={() => {
            setBlurred((prev) => new Set(prev).add(id))
          }}
        />
        {problem === null ? null : (
          <span className="lm-refraction__cell-error" role="alert">
            {problem.message}
          </span>
        )}
      </td>
    )
  }

  return (
    <section className="lm-refractions" aria-labelledby="refractions-heading">
      <header className="lm-refractions__top">
        <h2 id="refractions-heading" className="lm-refractions__heading">
          {t('refraction.heading')}
        </h2>
        {!canRecord || open ? null : (
          <button
            type="button"
            className="lm-button lm-button--primary"
            onClick={() => {
              setOpen(true)
            }}
          >
            {t('refraction.start')}
          </button>
        )}
      </header>

      {!canView ? null : loadFailed ? (
        <p className="lm-error" role="alert">
          {t('refraction.loadFailed')}
        </p>
      ) : records.length === 0 ? (
        <div className="lm-refractions__empty">
          <p className="lm-refractions__empty-title">{t('refraction.none')}</p>
          <p className="lm-hint">{t('refraction.noneHint')}</p>
        </div>
      ) : (
        <ul className="lm-refractions__list">
          {records.map((r) => {
            const referred = r.outcome === 'referred_out'
            return (
              <li key={r.id}>
                <article className="lm-refraction" aria-label={day(r.performedAt)}>
                  <header className="lm-refraction__head">
                    <div>
                      <p className="lm-refraction__date">{day(r.performedAt)}</p>
                      <p className="lm-refraction__tester">
                        {(TESTER_KEY as Record<string, string>)[r.tester.kind] === undefined
                          ? r.tester.kind
                          : t(TESTER_KEY[r.tester.kind as RefractionTester])}
                        {r.tester.name === undefined ? '' : `: ${r.tester.name}`}
                        {r.tester.registration === undefined ? '' : ` (${r.tester.registration})`}
                      </p>
                    </div>
                    <span className="lm-chip lm-refraction__outcome" data-outcome={r.outcome}>
                      {(OUTCOME_KEY as Record<string, string>)[r.outcome] === undefined
                        ? r.outcome
                        : t(OUTCOME_KEY[r.outcome as RefractionOutcome])}
                    </span>
                  </header>

                  {!referred || r.referral === undefined ? null : (
                    <p className="lm-refraction__referral">
                      {t('refraction.referredLine', {
                        destination: r.referral.referredTo,
                        on: day(r.referral.referredOn),
                      })}
                    </p>
                  )}

                  <div className="lm-refraction__tables" dir="ltr">
                    <table className="lm-refraction__table">
                      <caption>{t('refraction.section.acuity')}</caption>
                      <thead>
                        <tr>
                          <th scope="col">{t('rx.col.eye')}</th>
                          <th scope="col">{t('refraction.va.unaided')}</th>
                          <th scope="col">{t('refraction.va.aided')}</th>
                          <th scope="col">{t('refraction.va.pinhole')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {EYES.map((side) => (
                          <tr key={side}>
                            <th scope="row">{sideLabel(side)}</th>
                            <td>{acuityText(r[side]?.unaided)}</td>
                            <td>{acuityText(r[side]?.aided)}</td>
                            <td>{acuityText(r[side]?.pinhole)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <table className="lm-refraction__table">
                      <caption>{t('refraction.section.readings')}</caption>
                      <thead>
                        <tr>
                          <th scope="col">{t('rx.col.eye')}</th>
                          <th scope="col">{t('refraction.set.objective')}</th>
                          <th scope="col">{t('refraction.set.subjective')}</th>
                          <th scope="col">{t('refraction.set.oldGlasses')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {EYES.map((side) => (
                          <tr key={side}>
                            <th scope="row">{sideLabel(side)}</th>
                            <td>{setCell(r[side]?.objective)}</td>
                            <td>{setCell(r[side]?.subjective)}</td>
                            <td>{setCell(r[side]?.oldGlasses)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {r.nearAdd === undefined && r.dominantEye === undefined ? null : (
                    <p className="lm-refraction__facts">
                      {r.nearAdd === undefined
                        ? null
                        : t('refraction.nearLine', {
                            add: formatPower(r.nearAdd),
                            distance: r.workingDistanceCm ?? 0,
                          })}
                      {r.nearAdd !== undefined && r.dominantEye !== undefined ? ' · ' : null}
                      {r.dominantEye === undefined
                        ? null
                        : t('refraction.dominantLine', {
                            eye: sideLabel(r.dominantEye === 'od' ? 'od' : 'os'),
                          })}
                    </p>
                  )}
                  {r.prescriptionIds.length === 0 ? null : (
                    <p className="lm-refraction__produced">{t('refraction.producedRx')}</p>
                  )}
                </article>
              </li>
            )
          })}
        </ul>
      )}

      {!canRecord || !open ? null : (
        <form
          className="lm-refraction-form"
          aria-label={t('refraction.formLabel')}
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void record()
          }}
        >
          {error === null ? null : (
            <p
              className="lm-error lm-refraction-form__error"
              role="alert"
              ref={errorRef}
              tabIndex={-1}
            >
              {error}
            </p>
          )}

          <fieldset className="lm-refraction-form__section">
            <legend>
              <span className="lm-refraction-form__step">1</span> {t('refraction.section.who')}
            </legend>
            <div className="lm-seg" role="radiogroup" aria-label={t('refraction.section.who')}>
              {REFRACTION_TESTERS.map((k) => (
                <label
                  key={k}
                  className="lm-seg__option"
                  data-checked={kind === k ? 'true' : 'false'}
                >
                  <input
                    type="radio"
                    name="refraction-tester"
                    value={k}
                    checked={kind === k}
                    onChange={() => {
                      setKind(k)
                    }}
                  />
                  <span className="lm-seg__title">{t(TESTER_KEY[k])}</span>
                  <span className="lm-seg__help">{t(TESTER_HELP_KEY[k])}</span>
                </label>
              ))}
            </div>
            {inHouse ? <p className="lm-hint">{t('refraction.recordedAsYou')}</p> : null}
            {kind === 'customer_supplied_unknown' ? (
              <p className="lm-hint">{t('refraction.unknownFlag')}</p>
            ) : null}
            <div className="lm-refraction-form__row">
              {needsName ? (
                <label className="lm-field">
                  <span className="lm-field__label">{t('refraction.testerName')}</span>
                  <input
                    className="lm-input"
                    type="text"
                    autoComplete="off"
                    value={testerName}
                    onChange={(e) => {
                      setTesterName(e.currentTarget.value)
                    }}
                  />
                </label>
              ) : null}
              {needsRegistration ? (
                <label className="lm-field">
                  <span className="lm-field__label">{t('refraction.registration')}</span>
                  <input
                    className="lm-input"
                    type="text"
                    autoComplete="off"
                    dir="ltr"
                    value={registration}
                    onChange={(e) => {
                      setRegistration(e.currentTarget.value)
                    }}
                  />
                </label>
              ) : null}
            </div>
          </fieldset>

          <fieldset className="lm-refraction-form__section">
            <legend>
              <span className="lm-refraction-form__step">2</span> {t('refraction.section.acuity')}
            </legend>
            <div
              className="lm-seg lm-seg--compact"
              role="radiogroup"
              aria-label={t('refraction.notation')}
            >
              {VISUAL_ACUITY_NOTATIONS.map((n) => (
                <label
                  key={n}
                  className="lm-seg__option"
                  data-checked={notation === n ? 'true' : 'false'}
                >
                  <input
                    type="radio"
                    name="refraction-notation"
                    value={n}
                    checked={notation === n}
                    onChange={() => {
                      setNotation(n)
                    }}
                  />
                  <span className="lm-seg__title">{t(NOTATION_KEY[n])}</span>
                </label>
              ))}
            </div>
            <p className="lm-hint">{t('refraction.notationHint')}</p>
            <table className="lm-refraction-form__grid" dir="ltr">
              <thead>
                <tr>
                  <th scope="col">{t('rx.col.eye')}</th>
                  <th scope="col">{t('refraction.va.unaided')}</th>
                  <th scope="col">{t('refraction.va.aided')}</th>
                  <th scope="col">{t('refraction.va.pinhole')}</th>
                </tr>
              </thead>
              <tbody>
                {EYES.map((side) => (
                  <tr key={side}>
                    <th scope="row">{sideLabel(side)}</th>
                    {acuityCell(side, 'unaided')}
                    {acuityCell(side, 'aided')}
                    {acuityCell(side, 'pinhole')}
                  </tr>
                ))}
              </tbody>
            </table>
          </fieldset>

          <fieldset className="lm-refraction-form__section">
            <legend>
              <span className="lm-refraction-form__step">3</span> {t('refraction.section.readings')}
            </legend>
            <p className="lm-hint">{t('refraction.stepHint')}</p>
            {SETS.map((name) => (
              <div
                key={name}
                className="lm-refraction-form__set"
                role="group"
                aria-label={t(SET_LABEL[name])}
              >
                <h4 className="lm-refractions__subheading">{t(SET_LABEL[name])}</h4>
                <table className="lm-refraction-form__grid" dir="ltr">
                  <thead>
                    <tr>
                      <th scope="col">{t('rx.col.eye')}</th>
                      <th scope="col">{t('rx.col.sphere')}</th>
                      <th scope="col">{t('rx.col.cylinder')}</th>
                      <th scope="col">{t('rx.col.axis')}</th>
                      {name === 'oldGlasses' ? <th scope="col">{t('rx.col.add')}</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {EYES.map((side) => {
                      const set = eyes[side][name]
                      const label = (col: string): string =>
                        `${col}, ${t(SET_LABEL[name])}, ${sideLabel(side)}`
                      const field = (
                        key: keyof SetForm,
                        column: Parameters<typeof t>[0],
                        placeholder: string,
                      ) => (
                        <td>
                          {cell(
                            label(t(column)),
                            set[key],
                            (v) => {
                              setEyeField(side, name, key, v)
                            },
                            placeholder,
                          )}
                        </td>
                      )
                      return (
                        <tr key={side}>
                          <th scope="row">{sideLabel(side)}</th>
                          {field('sphere', 'rx.col.sphere', '+0.00')}
                          {field('cylinder', 'rx.col.cylinder', '-0.00')}
                          {field('axis', 'rx.col.axis', '0-180')}
                          {name === 'oldGlasses' ? field('add', 'rx.col.add', '+0.00') : null}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </fieldset>

          <fieldset className="lm-refraction-form__section">
            <legend>
              <span className="lm-refraction-form__step">4</span> {t('refraction.section.near')}
            </legend>
            <div className="lm-refraction-form__row">
              <label className="lm-field">
                <span className="lm-field__label">{t('refraction.nearAdd')}</span>
                <input
                  className="lm-input"
                  type="text"
                  inputMode="decimal"
                  dir="ltr"
                  autoComplete="off"
                  placeholder="+2.00"
                  value={nearAdd}
                  onChange={(e) => {
                    setNearAdd(e.currentTarget.value)
                  }}
                />
              </label>
              <label className="lm-field">
                <span className="lm-field__label">{t('refraction.workingDistance')}</span>
                <input
                  className="lm-input"
                  type="text"
                  inputMode="numeric"
                  dir="ltr"
                  autoComplete="off"
                  placeholder="40"
                  value={workingDistance}
                  onChange={(e) => {
                    setWorkingDistance(e.currentTarget.value)
                  }}
                />
              </label>
            </div>
            <div
              className="lm-seg lm-seg--compact"
              role="radiogroup"
              aria-label={t('refraction.dominant')}
            >
              {(['', ...EYES] as const).map((side) => (
                <label
                  key={side}
                  className="lm-seg__option"
                  data-checked={dominantEye === side ? 'true' : 'false'}
                >
                  <input
                    type="radio"
                    name="refraction-dominant"
                    value={side}
                    checked={dominantEye === side}
                    onChange={() => {
                      setDominantEye(side)
                    }}
                  />
                  <span className="lm-seg__title">
                    {side === '' ? t('common.notRecorded') : sideLabel(side)}
                  </span>
                </label>
              ))}
            </div>
            <p className="lm-hint">{t('refraction.dominant')}</p>
          </fieldset>

          <fieldset className="lm-refraction-form__section">
            <legend>
              <span className="lm-refraction-form__step">5</span> {t('refraction.section.outcome')}
            </legend>
            <div className="lm-seg" role="radiogroup" aria-label={t('refraction.outcome')}>
              {REFRACTION_OUTCOMES.map((o) => (
                <label
                  key={o}
                  className="lm-seg__option"
                  data-checked={outcome === o ? 'true' : 'false'}
                >
                  <input
                    type="radio"
                    name="refraction-outcome"
                    value={o}
                    checked={outcome === o}
                    onChange={() => {
                      setOutcome(o)
                    }}
                  />
                  <span className="lm-seg__title">{t(OUTCOME_KEY[o])}</span>
                </label>
              ))}
            </div>
            {outcome === 'referred_out' ? (
              <div className="lm-refraction-form__referral">
                <div className="lm-refraction-form__row">
                  <label className="lm-field">
                    <span className="lm-field__label">{t('refraction.referredTo')}</span>
                    <input
                      className="lm-input"
                      type="text"
                      autoComplete="off"
                      value={referredTo}
                      onChange={(e) => {
                        setReferredTo(e.currentTarget.value)
                      }}
                    />
                  </label>
                  <label className="lm-field">
                    <span className="lm-field__label">{t('refraction.referredOn')}</span>
                    <input
                      className="lm-input"
                      type="date"
                      value={referredOn}
                      onChange={(e) => {
                        setReferredOn(e.currentTarget.value)
                      }}
                    />
                  </label>
                </div>
                <p className="lm-hint">{t('refraction.noReason')}</p>
              </div>
            ) : null}
          </fieldset>

          <div className="lm-refraction-form__bar">
            <p className="lm-queue-state" role="status">
              {line}
            </p>
            <div className="lm-screen__actions">
              <button
                type="button"
                className="lm-button lm-button--secondary"
                onClick={() => {
                  setOpen(false)
                  setError(null)
                }}
              >
                {t('refraction.cancel')}
              </button>
              <button
                type="submit"
                className="lm-button lm-button--primary"
                disabled={writeId !== null}
              >
                {t('refraction.record')}
              </button>
            </div>
          </div>
        </form>
      )}
    </section>
  )
}
